// Retaking an evidence point's view (CONDITION-REPORTS.md "Same-view photos"):
// the live camera, full screen, with the reference photo faded over it, and how
// far away and which way to turn. Outdoors GPS and the compass bring the
// assessor within a few metres and degrees; the overlay lines it up by eye.
// The shot is saved like any photo taken here, linked to the point. The app
// drives the camera itself (the iPad's own camera screen can't show an overlay),
// so this needs the camera allowed for the app; the reference needs signal.
import { useEffect, useRef, useState } from 'react';
import { Camera, Loader2, X } from 'lucide-react';
import { Button, Dialog, DialogContent, DialogTitle } from '@higgins/ui';
import { getEvidenceReference, type EvidencePointFromApi } from '../lib/api';
import { bearingNow } from '../lib/compass';
import { guidance, guidanceText } from '../lib/evidence-guide';
import { addPhoto } from '../lib/jobs';
import { armLocation, positionNow } from '../lib/location';

/** How often the guide reads the GPS and compass. */
const GUIDE_MS = 500;
const JPEG_QUALITY = 0.92;
const DEFAULT_FADE = 50;
const PERCENT = 100;

/** The video's current frame as a JPEG file. */
async function frameOf(video: HTMLVideoElement, name: string): Promise<File | null> {
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext('2d')?.drawImage(video, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
  return blob ? new File([blob], name, { type: 'image/jpeg' }) : null;
}

export function RetakeCamera({ visitId, jobId, areaId, point, onClose }: { visitId: string; jobId: string; areaId: string; point: EvidencePointFromApi; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [fade, setFade] = useState(DEFAULT_FADE);
  const [guide, setGuide] = useState(() => guidance(null, null, point));
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // The camera: the back one, released when the view closes.
  useEffect(() => {
    let stream: MediaStream | null = null;
    let closed = false;
    armLocation();
    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then((s) => {
        if (closed) return s.getTracks().forEach((t) => t.stop());
        stream = s;
        if (video.current) video.current.srcObject = s;
      })
      .catch(() => setProblem('The camera isn’t available: allow it for this app in the iPad’s settings, then try again.'));
    return () => {
      closed = true;
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  // The reference photo, as an image the overlay can show.
  useEffect(() => {
    let url: string | null = null;
    getEvidenceReference(visitId, point.id).then(
      (blob) => {
        url = URL.createObjectURL(blob);
        setReference(url);
      },
      () => setProblem('Last time’s photo couldn’t be loaded (it needs signal). You can still take the photo.'),
    );
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [visitId, point.id]);

  // The guide: distance and turn, twice a second.
  useEffect(() => {
    const timer = window.setInterval(() => setGuide(guidance(positionNow(), bearingNow(), point)), GUIDE_MS);
    return () => window.clearInterval(timer);
  }, [point]);

  async function take() {
    if (!video.current) return;
    setSaving(true);
    try {
      const file = await frameOf(video.current, `${point.name || 'Retake'}.jpg`);
      if (!file) throw new Error('The camera gave no picture.');
      const place = { takenAt: new Date().toISOString(), bearing: bearingNow(), position: positionNow(), method: 'Camera' as const, camera: null };
      await addPhoto(jobId, areaId, file, null, place, { hig_evidencepointid: point.id });
      onClose();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'The photo wasn’t saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby={undefined} className="inset-0 flex h-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none bg-black p-0 text-white shadow-none sm:rounded-none [&>button:last-child]:hidden">
        <header className="flex items-center gap-3 px-4 pb-2 pt-[max(0.75rem,env(safe-area-inset-top))]">
          <DialogTitle className="min-w-0 flex-1 truncate text-base font-medium text-white">Retake: {point.name}</DialogTitle>
          <button type="button" onClick={onClose} aria-label="Close the camera" className="flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-slate-900">
            <X className="h-6 w-6" />
          </button>
        </header>
        <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
          <video ref={video} autoPlay playsInline muted className="max-h-full max-w-full" />
          {reference && <img src={reference} alt="Last time’s photo" className="pointer-events-none absolute inset-0 m-auto max-h-full max-w-full" style={{ opacity: fade / PERCENT }} />}
          <p className={`absolute left-1/2 top-3 -translate-x-1/2 rounded-full px-3 py-1 text-sm ${guide.onPoint ? 'bg-green-700' : 'bg-black/60'}`}>
            {guide.onPoint ? 'On the point · ' : ''}
            {guidanceText(guide)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2">
          <label className="flex flex-1 items-center gap-2 text-sm">
            Last time&rsquo;s photo
            <input type="range" min={0} max={PERCENT} value={fade} onChange={(e) => setFade(Number(e.target.value))} className="flex-1" aria-label="How strongly last time’s photo shows" />
          </label>
          <Button className="min-h-11" disabled={saving} onClick={() => void take()}>
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Camera className="mr-1 h-4 w-4" />} Take photo
          </Button>
          {problem && <p className="w-full text-sm text-amber-300">{problem}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
